import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Button, InlineLoading, Tooltip } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useStore } from '@tanstack/react-form';
import moment from 'moment';
import { formatDuration } from 'utils/duration';
import { withSubtractCoverage } from 'utils/subtract-coverage';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from 'components/accordion/accordion';
import ErrorToast from 'components/error-toast';
import { CaseManagement } from './case-management';
import { CoverageConflicts } from './dps-coverage/coverage-conflicts';
import { CasePickerDrillIn } from './case-picker-drill-in';
import { EvalSettingsForm } from './eval-settings-form';
import { OutputDisplayControls } from './output-display-controls';
import { PriceDeckPickerDrillIn } from './price-deck-picker-drill-in';
import { ScenarioPickerDrillIn } from './scenario-picker-drill-in';
import { UncoveredDrillIn } from './uncovered-drill-in';
import { useEval3Page } from './context';
import {
  type WorkingConfigForm,
  useCalcDetail,
  useCalculate,
  useEvaluationCaseDetail,
  usePartialTracts,
  useRunScenarios,
  useTypeCurveParams,
} from './hooks';
import {
  buildCalculateRequest,
  interestItemsDiverged,
  runVerdictText,
} from './utils';
import {
  electionGroupsOf,
  electionTermsMissing,
} from './interest-modeling-election';
import { carryTermsMissing } from './interest-modeling-carry';
import { evalSettingsPanelOpenAtom } from './atoms';

/**
 * The tract names for a Calculate-gate reason: up to three, then "and N more".
 * Read off the items themselves, falling back to the tract id when an item carries
 * no stored display name; a Tract the package holds in part is followed by its
 * "(N of M Subtracts)", as Interest Modeling names it.
 *
 * @param {InterestItem[]} items - The gating interest items.
 * @param {Function} labelForTract - A tract's label (`usePartialTracts().labelForTract`).
 * @return {string} The comma-joined list.
 */
const tractList = (
  items: InterestItem[],
  labelForTract: (tractId: string) => string | null
): string => {
  const names = items.map((item) =>
    withSubtractCoverage(item.name ?? item.tractId, labelForTract(item.tractId))
  );
  const shown = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${shown} and ${names.length - 3} more` : shown;
};
import type {
  CasePickerState,
  EvaluationCaseCoverage,
  InterestItem,
  PanelView,
  ScenarioPickerContext,
} from './types';
import styles from './eval-settings-panel.module.scss';

/** The picker's initial slider state (mock default: min coverage 95, collapsed). */
const DEFAULT_PICKER_STATE: CasePickerState = { threshold: 95, showAll: false };

export interface EvalSettingsPanelProps {
  /** Whether the panel is open (expanded). Open by default at the page level. */
  open: boolean;
  /** Close request (the header × and the footer Cancel) — the owner clears `open`. */
  onRequestClose: () => void;
  /** Run a calculation (the footer Calculate). */
  onCalculate: () => void;
  /** Disable Calculate (not dirty / no draft / invalid effective date — resolved by the owner). */
  calculateDisabled: boolean;
  /** Optional reason shown as the disabled Calculate button's tooltip. */
  calculateDisabledReason?: string;
  /** A run is in flight — the footer shows the phase label in place of Calculate. */
  isCalculating: boolean;
  /**
   * What the in-flight run is currently doing. A run on a large package takes 4-5 minutes,
   * split between the job resolving scenarios and the calculator computing (INS-12398), and a
   * single unchanging "Calculating…" across all of it reads as a hang. Naming the phase is
   * what tells the user it is advancing — deliberately not an ETA, which the data does not
   * yet support.
   */
  calculatingLabel?: string;
  /**
   * Case-management section content — rendered inside a second "Case management"
   * accordion below Evaluation settings, open by default (P4 FR#1). A slot
   * (mirroring `children`) so this component stays domain-free.
   */
  caseManagement?: ReactNode;
  /**
   * The active drill-in surface (case picker / scenario picker / uncovered). When
   * set, the BODY renders it INSTEAD of the accordions — the header and the
   * Cancel | Calculate footer stay visible throughout (P4 FR#14).
   */
  drillIn?: ReactNode;
  /** The Evaluation-settings form + display controls, injected by the container. */
  children: ReactNode;
}

/**
 * The right Eval Settings panel — a purely presentational inline column that animates
 * its width (480 ↔ 0) open/closed (mock §9). Header ("Eval Settings" + close), a body
 * holding the open-by-default "Evaluation settings" accordion (renders `children` — the
 * config form + display controls) and, when `caseManagement` is provided, a second
 * open-by-default "Case management" accordion beneath it (P4 FR#1), and a
 * `Cancel | Calculate` footer. When `drillIn` is set the body renders IT instead of
 * the accordions — header + footer stay (the P4 drill-in navigation model, FR#14).
 * Reads no atoms: `open` / `onRequestClose` / `onCalculate` and the footer flags are
 * all props; the container owns the panel-open atom and the Calculate gate.
 *
 * Open-by-default snaps in with NO initial animation: the width transition is withheld
 * until after the first paint (`animated`), so only user-driven open/close toggles
 * animate.
 *
 * @param {EvalSettingsPanelProps} props - Open state + close/calculate handlers + footer flags + the body slots.
 * @return {JSX.Element} The Eval Settings panel column.
 */
export function EvalSettingsPanel({
  open,
  onRequestClose,
  onCalculate,
  calculateDisabled,
  calculateDisabledReason,
  isCalculating,
  calculatingLabel = 'Calculating…',
  caseManagement,
  drillIn,
  children,
}: EvalSettingsPanelProps) {
  // Withhold the width transition until after the first paint so an open-by-default
  // panel snaps into place instead of animating in on mount.
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    setAnimated(true);
  }, []);

  const calculateButton = (
    <Button
      kind="primary"
      size="lg"
      disabled={calculateDisabled}
      onClick={onCalculate}
    >
      Calculate
    </Button>
  );

  return (
    <aside
      className={classNames(styles.panel, {
        [styles.panelClosed]: !open,
        [styles.animated]: animated,
      })}
      aria-hidden={!open}
      aria-label="Eval Settings"
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <h2 className={styles.heading}>Eval Settings</h2>
            <p className={styles.subheading}>Configure evaluation inputs</p>
          </div>
          <Button
            hasIconOnly
            size="sm"
            kind="ghost"
            onClick={onRequestClose}
            iconDescription="Close"
            tooltipAlignment="end"
            tooltipPosition="left"
            renderIcon={(props) => <Close size={20} {...props} />}
          />
        </div>

        <div className={styles.body}>
          {drillIn ?? (
            <div className={styles.accordionStack}>
              <Accordion className={styles.accordion} defaultExpanded>
                <AccordionSummary>
                  <span className={styles.accordionTitle}>
                    Evaluation settings
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  {/* A gapped stack so the two injected components (display controls + form)
                      are spaced the same as the rows within each — uniform top-to-bottom
                      rhythm across the panel-seam (mock §9). */}
                  <div className={styles.settingsStack}>{children}</div>
                </AccordionDetails>
              </Accordion>
              {caseManagement !== undefined && (
                <Accordion className={styles.accordion} defaultExpanded>
                  <AccordionSummary>
                    <span className={styles.accordionTitle}>
                      Case management
                    </span>
                  </AccordionSummary>
                  <AccordionDetails>{caseManagement}</AccordionDetails>
                </Accordion>
              )}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button kind="secondary" size="lg" onClick={onRequestClose}>
            Cancel
          </Button>
          {isCalculating ? (
            <InlineLoading
              className={styles.calculating}
              description={calculatingLabel}
            />
          ) : calculateDisabled && calculateDisabledReason ? (
            // A disabled Button emits no hover, so a focusable span wraps it as the
            // tooltip trigger — the reason stays discoverable on hover / focus.
            <Tooltip label={calculateDisabledReason} align="top">
              <span className={styles.calculateTrigger} tabIndex={0}>
                {calculateButton}
              </span>
            </Tooltip>
          ) : (
            calculateButton
          )}
        </div>
      </div>
    </aside>
  );
}

export interface EvalSettingsPanelSectionProps {
  /** The lifted working-config form (owned by `Eval3NewContent`, shared with the table). */
  workingConfig: WorkingConfigForm;
}

/**
 * Seconds this run has been going, for formatDuration to render, or null when nothing is
 * running.
 *
 * Anchored on the RUN, not on the viewed calc's `createdAt`, which is wrong twice over:
 *
 *   * While the POST is in flight the cache still holds the PREVIOUSLY VIEWED eval, so its
 *     `createdAt` can be hours old — a fresh click rendered "Starting… 13h 2m 7s".
 *   * The 202 seed spreads the previous detail and never sets `createdAt` (it is not known
 *     until the completion refetch — see useCalculate's onSuccess), so on a NEW eval there
 *     is no date at all and the timer simply disappeared.
 *
 * So: when this tab started the run, measure from the moment it started. `createdAt` is used
 * only when we did NOT start it — a reload or a second tab landing on an already-running
 * eval, where the server's timestamp is both correct and the only thing available.
 *
 * @param {boolean} active - Whether a run is in flight; false stops and resets the clock.
 * @param {boolean} posting - Whether this tab's Calculate POST is in flight. Its rising edge
 *   is what marks the run as ours; an `active` edge cannot, because the first render has no
 *   detail yet, so `active` goes false -> true on an ordinary mount into a running eval.
 * @param {string | undefined} serverStartedAt - The viewed calc's `createdAt`, used only for
 *   a run this tab did not start.
 * @return {number | null} Elapsed seconds, or null when nothing is running.
 */
function useElapsedSeconds(
  active: boolean,
  posting: boolean,
  serverStartedAt: string | undefined
): number | null {
  const [now, setNow] = useState(() => Date.now());
  // When THIS tab started the run. Null means we arrived mid-run and must trust the server.
  const startedHereRef = useRef<number | null>(null);

  useEffect(() => {
    if (posting) startedHereRef.current ??= Date.now();
  }, [posting]);

  useEffect(() => {
    if (!active) {
      startedHereRef.current = null;
      return undefined;
    }

    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!active) return null;

  const parsedServer = serverStartedAt
    ? new Date(serverStartedAt).getTime()
    : Number.NaN;
  const started = startedHereRef.current ?? parsedServer;
  if (!Number.isFinite(started)) return null;

  // Clamp: a client clock behind the server's would otherwise render a negative duration.
  return Math.max(0, (now - started) / 1000);
}

export function EvalSettingsPanelSection({
  workingConfig,
}: EvalSettingsPanelSectionProps) {
  const {
    packageId,
    calculationId,
    workspaceId: routeWorkspaceId,
    isDraft,
  } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);

  const [open, setOpen] = useAtom(evalSettingsPanelOpenAtom);

  // P4 drill-in navigation (FR#14): ONE panel body swapping main ↔ the three
  // drill-ins. useState (not an atom) — single owner, every writer is in this
  // subtree.
  const [panelView, setPanelView] = useState<PanelView>({ view: 'main' });
  // The picker's slider state, lifted HERE (not kept in the picker) so a
  // Picker → Uncovered(preview) → Picker round-trip preserves it (FR#14).
  const [pickerState, setPickerState] =
    useState<CasePickerState>(DEFAULT_PICKER_STATE);

  // Gate, read reactively off the form store: enabled when the form is dirty OR a
  // from-scratch draft is active, AND the effective date is present + parseable, AND a
  // Scenario is selected (required to run — legacy parity), AND an evaluation case is
  // selected (P4 FR#11 — supersedes P5's immediately-calculatable fresh draft: one case
  // pick is now required). The date range does NOT gate (display-only). These `useStore`
  // subscriptions live here so a form edit re-renders only this subtree.
  const isDirty = useStore(workingConfig.form.store, (state) => state.isDirty);
  const effectiveDate = useStore(
    workingConfig.form.store,
    (state) => state.values.effectiveDate
  );
  const discountToDate = useStore(
    workingConfig.form.store,
    (state) => state.values.discountToDate
  );
  const discountToDateType = useStore(
    workingConfig.form.store,
    (state) => state.values.discountToDateType
  );
  const scenarioId = useStore(
    workingConfig.form.store,
    (state) => state.values.scenarioId
  );
  const evaluationCaseId = useStore(
    workingConfig.form.store,
    (state) => state.values.evaluationCaseId
  );
  // The run's own deck pick (`null` = the case's deck): what the deck picker opens checked on.
  const priceDeckId = useStore(
    workingConfig.form.store,
    (state) => state.values.priceDeckId
  );
  const interestItems = useStore(
    workingConfig.form.store,
    (state) => state.values.interestItems
  );
  // The Calculate messages describe the run about to start, so they label the package as it
  // stands, not an evaluation that may be open.
  const { labelForTract } = usePartialTracts({ ofPackage: true });
  // The active case's detail — the main-origin Uncovered header's eyebrow (the
  // case name). RQ dedupes with CaseManagement's read (same key); T12 also
  // feeds `casePriceDeckId` from here.
  const {
    data: activeCaseDetail,
    isError: activeCaseDetailFailed,
    refetch: refetchActiveCaseDetail,
  } = useEvaluationCaseDetail(evaluationCaseId);
  // Interest-modeling dirtiness derived from the COMMITTED baseline, not form meta:
  // the mid-run `form.reset` rebaseline (onRunCompleted) keeps concurrent interest
  // edits' values but eats their dirty meta, leaving `isDirty` false with edited
  // values on screen. Comparing against the committed blob survives any meta loss.
  const interestItemsDirty = interestItemsDiverged(
    interestItems,
    calcDetail?.configurations?.interest_items
  );
  const validEffectiveDate =
    effectiveDate !== '' && moment(effectiveDate, 'YYYY-MM-DD', true).isValid();
  // Discounting to a date BEFORE the effective date floors every time delta at zero
  // and collapses the valuation toward a plain nominal sum — no error, no visual cue.
  // The picker's `minDate` stops it being PICKED; this rung stops it being RUN when it
  // arrives another way (a seeded run, a typed date). Blank is valid: it means "no
  // override", not "invalid".
  // Scoped to 'fixed': it's the only mode that puts a date on the wire, and the other
  // two clear the field anyway — but reading the mode here means a stale date can never
  // gate a run that isn't sending one.
  const discountToDateBeforeEffective =
    discountToDateType === 'fixed' &&
    discountToDate !== '' &&
    validEffectiveDate &&
    moment(discountToDate, 'YYYY-MM-DD', true).isValid() &&
    moment(discountToDate, 'YYYY-MM-DD').isBefore(
      moment(effectiveDate, 'YYYY-MM-DD')
    );
  // 'fixed' with the date cleared is half-filled: the builder would send no override at
  // all, silently running as "use case default" while the control still reads Fixed.
  // Block it here instead, so the contradiction is visible rather than resolved behind
  // the user's back.
  const fixedDiscountToDateMissing =
    discountToDateType === 'fixed' && discountToDate === '';
  const hasScenario = scenarioId !== null;
  const hasCase = evaluationCaseId !== null;
  // A UMI election with no outcome chosen contributes nothing to the run, so committing
  // it would bake in a no-op position (and reopen would restore an unmodeled UMI). Block
  // the run until every election is either elected or removed, and name the exact
  // tract(s) so the reason points straight at the fix. Carries always contribute, so they
  // never gate; the name is read off the item itself (no fetch), falling back to the id.
  // An election whose tract's wells disagree on our interest is seeded with NO
  // participate terms (INS-10673) — there is no single honest number. It is already
  // blocked downstream (incomplete ⇒ no preview ⇒ nothing to elect), but the
  // un-elected rung would explain it as "select an election" when the real blocker
  // is the unentered interest. This rung ranks AHEAD of it so the reason names the
  // actual fix.
  const untermedElections = interestItems.filter(electionTermsMissing);
  const untermedElectionReason =
    untermedElections.length > 0
      ? `Enter the participating interest for ${tractList(
          untermedElections,
          labelForTract
        )} in Interest Modeling to calculate.`
      : undefined;
  // A carry group holding properties but no after-payout terms would reach the
  // calculator as a position with no interest attached. Carries otherwise never
  // gate, so this is the only thing standing between that group and a run.
  const untermedCarries = interestItems.filter(carryTermsMissing);
  const untermedCarryReason =
    untermedCarries.length > 0
      ? `Enter the after-payout interest for ${tractList(
          untermedCarries,
          labelForTract
        )} in Interest Modeling to calculate.`
      : undefined;
  // Every property group elects its own outcome (INS-10673), so a tract is only
  // settled once each of its filled groups has one. An EMPTY group elects nothing and
  // prices nothing — it is ignored here, as it is by the completeness gate.
  // An election whose groups hold NO properties prices nothing — it cannot reach the
  // un-elected rung below (there is no outcome to elect), so without this it would
  // slip every gate and calculate as though the tract were not modeled at all.
  const unassembledElections = interestItems.filter(
    (item) =>
      item.kind === 'election' &&
      !electionGroupsOf(item).some((group) => group.wellIds.length > 0)
  );
  const unassembledElectionReason =
    unassembledElections.length > 0
      ? `Add properties to a group for ${tractList(
          unassembledElections,
          labelForTract
        )} in Interest Modeling, or remove ${
          unassembledElections.length === 1 ? 'it' : 'them'
        }, to calculate.`
      : undefined;
  const unelectedElections = interestItems.filter(
    (item) =>
      item.kind === 'election' &&
      electionGroupsOf(item).some(
        (group) => group.wellIds.length > 0 && !group.electedElection
      )
  );
  const unelectedElectionReason =
    unelectedElections.length > 0
      ? (() => {
          const object = unelectedElections.length === 1 ? 'it' : 'them';
          return `Select an election for ${tractList(
            unelectedElections,
            labelForTract
          )} in Interest Modeling, or remove ${object}, to calculate.`;
        })()
      : undefined;
  // Tooltip ladder in FULL priority order (P4 FR#11): invalid date → discount-to
  // date missing under Fixed → discount-to date before the effective date → no
  // scenario → no case → case incomplete → carry group with no after-payout
  // terms → UMI election with no assembled group → UMI election with no participate
  // terms → un-elected UMI election → pristine.
  // `undefined` means every rung passes, and the disabled flag is DERIVED from it
  // below (single source — a future rung can't desync the button from its tooltip).
  // The effective Type Curve scenario (the case's own, INS-10669), published for the table /
  // catalogs / map. Resolved here because this component is mounted regardless of the panel's open
  // state, so the value cannot go stale behind a closed panel.
  useTypeCurveParams(workingConfig);

  // (13.2) A case's run starts only when every domain has a scenario and none is archived, with
  // this run's overrides — EvaluationCase::Readiness's verdict, read from run_scenarios rather than
  // worked out here. It ranks after the case rung; the case's Type Curve is one of its eight
  // domains, so there is no separate Type Curve rung.
  const runScenarios = useRunScenarios(workingConfig);
  const runData = runScenarios.isPreviousData ? undefined : runScenarios.data;
  // A failed refetch (on focus, say) keeps the answer it had for these same overrides, and the gate
  // keeps reading it: only a failure with no current answer blocks.
  const caseIncompleteReason = !hasCase
    ? undefined
    : runData !== undefined
      ? (runVerdictText(runData) ?? undefined)
      : runScenarios.isError
        ? `Couldn't check the case: ${runScenarios.error?.message ?? 'error'}`
        : 'Checking the case...';

  const calculateDisabledReason = !validEffectiveDate
    ? 'Set a valid effective date to calculate.'
    : fixedDiscountToDateMissing
      ? 'Pick a discount to date, or choose another Discount To Date option.'
      : discountToDateBeforeEffective
        ? 'Set a discount to date on or after the effective date to calculate.'
        : !hasScenario
          ? 'Select a Scenario before calculating.'
          : !hasCase
            ? 'Select an evaluation case before calculating.'
            : caseIncompleteReason
              ? caseIncompleteReason
              : untermedCarryReason
                ? untermedCarryReason
                : unassembledElectionReason
                  ? unassembledElectionReason
                  : untermedElectionReason
                    ? untermedElectionReason
                    : unelectedElectionReason
                      ? unelectedElectionReason
                      : !(isDirty || interestItemsDirty || isDraft)
                        ? 'Make a change to calculate.'
                        : undefined;
  const calculateDisabled = calculateDisabledReason !== undefined;

  const openCasePicker = () => setPanelView({ view: 'casePicker' });
  const openUncovered = () =>
    setPanelView({ view: 'uncovered', origin: 'main' });
  // Preview a picker row's gaps WITHOUT committing it — the origin carries the
  // previewed case so Back returns to the PICKER (its lifted slider state is
  // separate, so the round-trip preserves it, FR#14).
  const previewUncovered = (caseRow: EvaluationCaseCoverage) =>
    setPanelView({
      view: 'uncovered',
      origin: { previewCaseId: caseRow.id, previewCaseName: caseRow.name },
    });
  const openScenarioPicker = (context: ScenarioPickerContext) =>
    setPanelView({ view: 'scenarioPicker', context });
  const openPriceDeckPicker = () => setPanelView({ view: 'priceDeckPicker' });
  const backToMain = () => setPanelView({ view: 'main' });

  /**
   * Closes the panel (header × / footer Cancel) and resets the drill-in
   * navigation + the picker's slider state (FR#14 — reopening always lands on
   * the accordions with the picker back at its defaults).
   */
  const closePanel = () => {
    setOpen(false);
    backToMain();
    setPickerState(DEFAULT_PICKER_STATE);
  };

  /**
   * Commits a picked case: stores it on the working config (which resets the
   * per-run overrides + the explicit deck pick, FR#10) and returns to the main
   * view — the form is now dirty, so the case rung of the gate opens.
   *
   * @param {EvaluationCaseCoverage} caseRow - The picked coverage row.
   * @return {void}
   */
  const selectCase = (caseRow: EvaluationCaseCoverage): void => {
    workingConfig.setEvaluationCase(caseRow.id);
    backToMain();
  };

  /**
   * Commits a picked scenario as this run's override for the drill-in's domain, case-wide — the
   * form is now dirty (FR#8). The scenarioPicker view guarantees the context, so this is only
   * called from it. Re-picking the case's own scenario is written too; the row then reads as not
   * overridden, and nothing for it goes on the wire.
   *
   * Deliberately does NOT return to main: the picker stays open so the user can see the check land
   * on the row they clicked, and leaves via the header's back arrow (the only in-drill-in
   * navigation).
   *
   * @param {number} scenarioId - The picked scenario id.
   * @return {void}
   */
  const selectScenario = (scenarioId: number): void => {
    if (panelView.view !== 'scenarioPicker') {
      return;
    }
    workingConfig.setDomainOverride(panelView.context.domain, scenarioId);
  };

  /**
   * Commits a picked Price Deck as this run's deck (FR#9): it rides the run's overrides and the
   * top-level `priceDeckId` on Calculate. Picking the case's own deck clears the pick, so the run
   * is back on the case's deck and the row drops its "This run only" tag, as re-picking the case's
   * scenario does on the other rows. Stays open, like the scenario picker.
   *
   * @param {number} deckId - The picked deck id.
   * @return {void}
   */
  const selectPriceDeck = (deckId: number): void => {
    const caseDeckId = activeCaseDetail?.priceDeckId ?? null;
    workingConfig.setPriceDeckId(deckId === caseDeckId ? null : deckId);
  };

  /**
   * The active drill-in surface for the current view (each renders its own
   * DrillInHeader — the back arrow is the only in-drill-in navigation).
   *
   * @return {ReactNode} The drill-in node, or `undefined` on the main view (the
   *   accordions render).
   */
  const renderDrillIn = (): ReactNode => {
    switch (panelView.view) {
      case 'casePicker':
        return (
          <CasePickerDrillIn
            packageId={packageId}
            activeCaseId={evaluationCaseId}
            pickerState={pickerState}
            onPickerStateChange={setPickerState}
            onSelect={selectCase}
            onPreviewUncovered={previewUncovered}
            onBack={backToMain}
          />
        );
      case 'uncovered': {
        // Origin-routed (FR#6/FR#14): from the coverage tile it's the ACTIVE
        // case and Back returns to main; from a picker row it's the PREVIEWED
        // case (nothing committed) and Back returns to the picker.
        const { origin } = panelView;
        if (origin === 'main') {
          if (evaluationCaseId === null) {
            // Defensive — the tile only renders with a case selected.
            return undefined;
          }
          return (
            <UncoveredDrillIn
              packageId={packageId}
              caseId={evaluationCaseId}
              // undefined while the detail loads → the header suppresses the
              // eyebrow row instead of popping an empty span.
              caseName={activeCaseDetail?.name}
              isPreview={false}
              onBack={backToMain}
            />
          );
        }
        return (
          <UncoveredDrillIn
            packageId={packageId}
            caseId={origin.previewCaseId}
            caseName={origin.previewCaseName}
            isPreview
            onBack={() => setPanelView({ view: 'casePicker' })}
          />
        );
      }
      // The scenario-picker surface lands in its own slice (T14); its view is
      case 'scenarioPicker':
        return (
          <ScenarioPickerDrillIn
            context={panelView.context}
            onPick={selectScenario}
            onBack={backToMain}
          />
        );
      case 'priceDeckPicker':
        return (
          <PriceDeckPickerDrillIn
            // Unknown until the case's detail loads, which holds the picker (see its prop).
            caseDeckId={
              activeCaseDetail
                ? (activeCaseDetail.priceDeckId ?? null)
                : undefined
            }
            currentDeckId={priceDeckId ?? activeCaseDetail?.priceDeckId ?? null}
            caseDetailFailed={!activeCaseDetail && activeCaseDetailFailed}
            onRetryCaseDetail={() => refetchActiveCaseDetail()}
            onPick={selectPriceDeck}
            onBack={backToMain}
          />
        );
      default:
        return undefined;
    }
  };
  const drillIn = renderDrillIn();

  // The hook holds the form so it can rebaseline (reset) it on a new/fork 202 BEFORE
  // navigating — Calculate's own navigation then passes the unsaved-changes block.
  const {
    mutate: calculate,
    isPending: postInFlight,
    variables: postVariables,
    error: calculateError,
  } = useCalculate(workingConfig.form);
  // Scope the in-flight POST to the eval it was fired against (the mutation's own
  // `variables` — no extra client state): the mutation outlives an eval switch, so an
  // unscoped flag would show "Calculating…" on eval B while A's POST is still in
  // flight. `existingEvaluationId` is absent for a from-scratch draft, which matches
  // package mode's `calculationId === null`.
  const isPosting =
    postInFlight &&
    (postVariables?.existingEvaluationId ?? null) === calculationId;
  // "Calculating…" spans the whole run: from the CLICK (the POST in flight —
  // `isPosting`, the mutation's own state, not a client run registry) through the
  // server-derived `Processing` (the 202 seed flips the detail before `isPosting`
  // drops, so the two halves hand off with no gap). Starting at the click also
  // closes the double-click → double-POST window: the footer swaps the Calculate
  // button for the spinner the moment the first click lands.
  const isCalculating = isPosting || calcDetail?.status === 'Processing';
  // Elapsed time for the in-flight run, so the footer shows movement rather than a static
  // label for four to five minutes. Seconds, not a fixed unit: `0.08 min` for an eight-second
  // run reads worse than `8s`, and formatDuration switches to `4m 11s` where that becomes the
  // clearer form. Shared with the admin dashboards — see utils/duration.
  const elapsedSeconds = useElapsedSeconds(
    isCalculating,
    isPosting,
    calcDetail?.createdAt
  );

  // Which phase to name. `dispatched` is the BE's own record of whether the run has reached
  // the eval-calculator, so the label tracks the actual handover rather than a timer:
  //   * the POST is still in flight            -> Starting…
  //   * Processing, not yet dispatched         -> Resolving scenarios…   (94-222s measured)
  //   * Processing, dispatched                 -> Calculating…           (87-157s measured)
  // `dispatched` is optional on the type, so an older response (or a detail not yet refetched)
  // falls through to the generic label rather than claiming a phase we cannot verify.
  const calculatingPhase = isPosting
    ? 'Starting…'
    : calcDetail?.dispatched === false
      ? 'Resolving scenarios…'
      : 'Calculating…';
  // No time until there is a start we can trust: during the POST there is no record yet, and
  // a detail without `createdAt` would otherwise read 0s forever.
  const calculatingLabel =
    elapsedSeconds === null
      ? calculatingPhase
      : `${calculatingPhase} ${formatDuration(elapsedSeconds)}`;

  // The target workspace: the viewed eval's, else the route's `workspaceId` (a draft's
  // or the browsed folder's) — so Calculate from a draft / browse view creates the new
  // eval in that folder. `null` (bare package mode) coalesces to `undefined`, which the
  // `onCalculate` guard treats as "no target" and bails.
  const workspaceId = calcDetail?.workspaceId ?? routeWorkspaceId ?? undefined;

  // Force a new-eval fork — the create-new checkbox shows checked + disabled AND the payload flag
  // is forced true — when there's no eval to update in place (draft / fresh workspace,
  // `calculationId === null`) OR the viewed eval is SAVED. Recalculating a saved eval must always
  // fork, never overwrite its immutable snapshot in place (P8 Save).
  const mustCreateNew = calculationId === null || (calcDetail?.saved ?? false);

  /**
   * Builds the P5 request from the current form values + page context and triggers the
   * calculation. `existingEvaluationId` is sent only when editing an existing eval
   * (omitted for a from-scratch draft). `useCalculate` handles the 202 seed + navigate +
   * the run sync; this is just the trigger.
   */
  const onCalculate = () => {
    if (workspaceId === undefined) {
      return;
    }
    calculate(
      buildCalculateRequest(workingConfig.form.state.values, {
        workspaceId,
        packageId,
        // A case's run: the gate above guarantees the rows.
        ...(hasCase && runScenarios.data
          ? { caseLevelRows: runScenarios.data.domains }
          : {}),
        forceCreateNew: mustCreateNew,
        ...(calculationId !== null
          ? { existingEvaluationId: calculationId }
          : {}),
      })
    );
  };

  return (
    <>
      <EvalSettingsPanel
        open={open}
        onRequestClose={closePanel}
        onCalculate={onCalculate}
        calculateDisabled={calculateDisabled}
        calculateDisabledReason={calculateDisabledReason}
        isCalculating={isCalculating}
        calculatingLabel={calculatingLabel}
        drillIn={drillIn}
        caseManagement={
          <>
            <CaseManagement
              packageId={packageId}
              workingConfig={workingConfig}
              onOpenCasePicker={openCasePicker}
              onOpenUncovered={openUncovered}
              onOpenScenarioPicker={openScenarioPicker}
              onOpenPriceDeckPicker={openPriceDeckPicker}
            />
            {/* DPS coverage readout + override-conflict list (07-COV §5), rendered
                beside the case-coverage element it belongs with. Reads the same
                `calcDetail` this section already fetches (no new request); renders
                nothing on a non-DPS run (both fields absent) or when a DPS run is
                fully covered with no conflicts. */}
            {/* key on the viewed eval so an open drill-in can't survive an
                eval switch (the panel stays mounted across selections). */}
            <CoverageConflicts
              key={calculationId ?? 'draft'}
              coverage={calcDetail?.coverage}
              overrideConflicts={calcDetail?.overrideConflicts}
              calculationId={calculationId ?? undefined}
            />
          </>
        }
      >
        {/* Display block FIRST (Measurement → Range → Time Interval), then the form
            (Effective Date → create-new → Description/Notes) — matches the mock's
            top-to-bottom order and puts Time Interval adjacent to Effective Date. */}
        <OutputDisplayControls />
        {/* Force create-new (checkbox checked + disabled) when there's no eval to update in place
            (a draft / fresh workspace) OR the viewed eval is SAVED — see `mustCreateNew` above. */}
        <EvalSettingsForm
          workingConfig={workingConfig}
          mustCreateNew={mustCreateNew}
        />
      </EvalSettingsPanel>
      {/* POST failed to even start the run — show the BE-sent message (`useCalculate`
          unwraps it from the error body) so a user-fixable config failure (e.g. "No
          slots matched evaluation case areas") is explicit. The form stays dirty, so
          the subtitle reassures the user their changes are kept for a retry. */}
      {calculateError && (
        <ErrorToast
          key={calculateError.message}
          message={calculateError.message}
          subtitle="Your changes are kept — try again."
        />
      )}
    </>
  );
}
